import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Window {
  id: string
  appId: string
  title: string
  icon: string
  position: { x: number; y: number }
  size: { width: number; height: number }
  minSize: { width: number; height: number }
  state: 'normal' | 'minimized' | 'maximized'
  zIndex: number
}

interface WindowState {
  windows: Window[]
  activeWindowId: string | null
  zIndexCounter: number

  // Persisted preferences (saved to localStorage)
  savedPositions: Record<string, { x: number; y: number }>
  savedSizes: Record<string, { width: number; height: number }>
  closedApps: string[] // Apps user has closed (prevents auto-reopen)

  // Actions
  openWindow: (appId: string, title: string, icon?: string, options?: { autoOpen?: boolean }) => void
  closeWindow: (windowId: string) => void
  minimizeWindow: (windowId: string) => void
  maximizeWindow: (windowId: string) => void
  restoreWindow: (windowId: string) => void
  focusWindow: (windowId: string) => void
  updateWindowPosition: (windowId: string, x: number, y: number) => void
  updateWindowSize: (windowId: string, width: number, height: number) => void
  isAppClosed: (appId: string) => boolean
}

// Default window sizes by app type
const defaultSizes: Record<string, { width: number; height: number }> = {
  about: { width: 600, height: 450 },
  products: { width: 700, height: 500 },
  terminal: { width: 650, height: 400 },
  filemanager: { width: 700, height: 450 },
  calculator: { width: 320, height: 440 },
  notepad: { width: 500, height: 400 },
  contact: { width: 750, height: 550 },
  minesweeper: { width: 360, height: 480 },
  worldmap: { width: 700, height: 500 },
  'oregon-trail': { width: 700, height: 500 },
  tradewars: { width: 900, height: 600 },
  'ai-assistant': { width: 700, height: 500 },
  zelda: { width: 700, height: 640 },
  solitaire: { width: 540, height: 580 },
  chess: { width: 580, height: 520 },
  default: { width: 600, height: 400 },
}

let windowIdCounter = 0

export const useWindowStore = create<WindowState>()(
  persist(
    (set, get) => ({
      windows: [],
      activeWindowId: null,
      zIndexCounter: 100,
      savedPositions: {},
      savedSizes: {},
      closedApps: [],

      openWindow: (appId, title, icon = 'window', options = {}) => {
        const state = get()
        const { autoOpen = false } = options

        // If this is an auto-open and the user previously closed this app, don't open
        if (autoOpen && state.closedApps.includes(appId)) {
          return
        }

        // Check if window already open
        const existing = state.windows.find((w) => w.appId === appId)
        if (existing) {
          // Focus existing window
          get().focusWindow(existing.id)
          return
        }

        const newZIndex = state.zIndexCounter + 1

        // Use saved size or default
        const size = state.savedSizes[appId] ?? defaultSizes[appId] ?? defaultSizes.default ?? { width: 400, height: 300 }
        const id = `window-${++windowIdCounter}`

        // Use saved position or cascade
        const offset = (state.windows.length % 5) * 30
        const position = state.savedPositions[appId] || { x: 150 + offset, y: 50 + offset }

        const newWindow: Window = {
          id,
          appId,
          title,
          icon,
          position,
          size,
          minSize: { width: 280, height: 200 },
          state: 'normal',
          zIndex: newZIndex,
        }

        // If user manually opens an app, remove it from closedApps
        const newClosedApps = autoOpen
          ? state.closedApps
          : state.closedApps.filter((id) => id !== appId)

        set({
          windows: [...state.windows, newWindow],
          activeWindowId: id,
          zIndexCounter: newZIndex,
          closedApps: newClosedApps,
        })
      },

      closeWindow: (windowId) =>
        set((state) => {
          const closedWindow = state.windows.find((w) => w.id === windowId)
          const newWindows = state.windows.filter((w) => w.id !== windowId)

          // Track that this app was closed by the user
          const newClosedApps = closedWindow && !state.closedApps.includes(closedWindow.appId)
            ? [...state.closedApps, closedWindow.appId]
            : state.closedApps

          return {
            windows: newWindows,
            activeWindowId:
              state.activeWindowId === windowId
                ? newWindows[newWindows.length - 1]?.id || null
                : state.activeWindowId,
            closedApps: newClosedApps,
          }
        }),

      minimizeWindow: (windowId) =>
        set((state) => ({
          windows: state.windows.map((w) =>
            w.id === windowId ? { ...w, state: 'minimized' as const } : w
          ),
        })),

      maximizeWindow: (windowId) =>
        set((state) => ({
          windows: state.windows.map((w) =>
            w.id === windowId
              ? { ...w, state: w.state === 'maximized' ? 'normal' : 'maximized' }
              : w
          ),
        })),

      restoreWindow: (windowId) => {
        const state = get()
        const window = state.windows.find((w) => w.id === windowId)
        if (window?.state === 'minimized') {
          set((s) => ({
            windows: s.windows.map((w) =>
              w.id === windowId ? { ...w, state: 'normal' as const } : w
            ),
          }))
        }
        get().focusWindow(windowId)
      },

      focusWindow: (windowId) =>
        set((state) => {
          const newZIndex = state.zIndexCounter + 1
          return {
            windows: state.windows.map((w) =>
              w.id === windowId ? { ...w, zIndex: newZIndex } : w
            ),
            activeWindowId: windowId,
            zIndexCounter: newZIndex,
          }
        }),

      updateWindowPosition: (windowId, x, y) =>
        set((state) => {
          const window = state.windows.find((w) => w.id === windowId)
          return {
            windows: state.windows.map((w) =>
              w.id === windowId ? { ...w, position: { x, y } } : w
            ),
            // Save position by appId for persistence
            savedPositions: window
              ? { ...state.savedPositions, [window.appId]: { x, y } }
              : state.savedPositions,
          }
        }),

      updateWindowSize: (windowId, width, height) =>
        set((state) => {
          const window = state.windows.find((w) => w.id === windowId)
          return {
            windows: state.windows.map((w) =>
              w.id === windowId ? { ...w, size: { width, height } } : w
            ),
            // Save size by appId for persistence
            savedSizes: window
              ? { ...state.savedSizes, [window.appId]: { width, height } }
              : state.savedSizes,
          }
        }),

      isAppClosed: (appId) => get().closedApps.includes(appId),
    }),
    {
      name: 'persona-os-windows',
      version: 1, // Bump this to reset stale localStorage
      partialize: (state) => ({
        savedPositions: state.savedPositions,
        savedSizes: state.savedSizes,
        closedApps: state.closedApps,
      }),
      // Ensure windows array is never restored from localStorage
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<WindowState>),
        windows: [], // Always start with empty windows
        activeWindowId: null,
        zIndexCounter: 100,
      }),
    }
  )
)
